import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BrowserService, ModalService } from '@edassist/shared';
import {
	BehaviorSubject,
	forkJoin,
	Observable,
	distinctUntilChanged,
	map,
	combineLatest, skipWhile, observeOn, asyncScheduler
} from 'rxjs';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { User } from '../../models/user/user.interface';
import { LoginRequest } from '../../models/login/login-request.interface';
import {
	constants,
	HybridAppService,
	LocalStorageService,
	SessionStorageService,
	UserTypeService,
	WINDOW
} from '@edassist/common';
import { TokenService } from '../token/token.service';
import { UserConfigService } from '../user/config/user-config.service';
import { ClientLoginDetails, UserType } from '@edassist/api';
import { requestDataChanged, withCache } from '@ngneat/cashew';
import { Idle } from '@ng-idle/core';
import { AuthService } from '@auth0/auth0-angular';

const relativeAPIUrl = 'sessions';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
	currentUser: Observable<User>;
	isLoggedIn: boolean;
	isSessionTimeoutModalShown = false;
	isSsoLogout = false;
	isAuth0Authenticated = false;
	isAuthenticated$: Observable<boolean>;

	private readonly renderer: Renderer2;
	private readonly currentUserSubject: BehaviorSubject<User>;

	get currentUserValue(): User {
		return this.currentUserSubject.value;
	}

	constructor(
		@Inject(WINDOW) private readonly window: Window,
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly idle: Idle,
		private readonly modalService: ModalService,
		private readonly tokenService: TokenService,
		private readonly hybridAppService: HybridAppService,
		private readonly rendererFactory2: RendererFactory2,
		private readonly userConfigService: UserConfigService,
		private readonly auth0Service: AuthService
	) {
		this.renderer = this.rendererFactory2.createRenderer(null, null); // tslint:disable-line
		this.renderer.listen('document', 'mousemove', (evt) => {
			if (this.isSessionTimeoutModalShown) {
				this.idle.watch();
				this.modalService.closeModal(constants.modalIds.sessionTimeout);
				this.isSessionTimeoutModalShown = false;
			}
		});
		this.currentUserSubject = new BehaviorSubject<User>(
			LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
		);
		this.currentUser = this.currentUserSubject.asObservable();
		this.isLoggedIn = Boolean(
			this.currentUserValue && this.currentUserValue.sessionToken
		);

		this.isAuthenticated$ = combineLatest<[boolean, boolean]>([
			this.auth0Service.isAuthenticated$,
			this.tokenService.token$.pipe(
				map((token) => this.tokenService.isTokenValid(token)),
				distinctUntilChanged()
			)
		]).pipe(
			map(([isAuth0Authenticated, isEdassistTokenValid]) => isAuth0Authenticated || isEdassistTokenValid),
			distinctUntilChanged()
		);

		// Initialize Edassist token service watch
		this.tokenService.token$
			.pipe(
				map((token) => this.tokenService.isTokenValid(token)),
				skipWhile((isValid, index) => index === 0 && !isValid),
				distinctUntilChanged(),
				observeOn(asyncScheduler),
				tap(loggedIn => loggedIn ?
					this.tokenService.setTokenRefreshInterval() :
					this.logout())
			)
			.subscribe();
	}

	login(loginRequest: LoginRequest): Observable<{
		userDetails: User;
		tokens: { sessionToken: string; refreshToken: string };
	}> {
		return this.http
			.post<{
				userDetails: User;
				tokens: { sessionToken: string; refreshToken: string };
			}>(`${relativeAPIUrl}/login`, loginRequest)
			.pipe(
				tap((userResponse) => {
					if (userResponse && userResponse.tokens) {
						const user: User = {
							firstName: userResponse.userDetails.firstName,
							lastName: userResponse.userDetails.lastName,
							mostRecentLogin: userResponse.userDetails.mostRecentLogin,
							resetPassword: userResponse.userDetails.resetPassword,
							userId: userResponse.userDetails.userId,
							userTypeId: userResponse.userDetails.userTypeId,
							username: userResponse.userDetails.username,
							sessionToken: userResponse.tokens.sessionToken,
							refreshToken: userResponse.tokens.refreshToken
						};
						this.handleUserLogin(user);
					}
				})
			);
	}

	postAuth0Login(): Observable<{
		userDetails: User;
		clientFeatures: ClientLoginDetails
	}> {
		return this.http
			.post<{ userDetails: User; clientFeatures: ClientLoginDetails }>('login', {})
			.pipe(
				tap((user) => {
					LocalStorageService.setItem(constants.localStorageKeys.CURRENT_USER_HYBRID, {
						userDetails: user.userDetails
					});

					LocalStorageService.setItem(constants.localStorageKeys.CLIENT_DETAILS, {
							...(LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS) ?? {}),
							...user.clientFeatures
						}
					);
				})
			);
	}

	logout(): void {
		// Remove items from session storage, stop session timeout subscriptions
		// and timers, and remove logged in user value from local storage
		// Also  reload the page to clear any
		// app memory / observable / subject cache and data using location.reload().
		this.logoutCleanup();
		if (this.isAuth0Authenticated) {
			this.auth0Service.logout({
				logoutParams: {
					returnTo: this.window.location.origin + constants.routes.LOGIN_UI.ROOT,
				},
			});

			return;
		}

		this.router
			.navigate([constants.routes.login])
			.then(() => BrowserService.reload());
	}

	hybridAppLogout(
		navigateToBaseLogin = true,
		ssoEnabled = false,
		ssoLogoutUrl = ''
	): void {
		this.http.delete<void>(`${relativeAPIUrl}/logout`).subscribe(() => {
			ssoEnabled ? this.ssoLogoutCleanup() : this.logoutCleanup();
			if (this.isAuth0Authenticated) {
				this.auth0Service.logout(
					{
						logoutParams: {
							returnTo: this.window.location.origin + constants.routes.LOGIN_UI.WELCOME
						}
					}
				);
			}

			if (ssoEnabled) {
				this.window.location.href = ssoLogoutUrl;
			}
			if (navigateToBaseLogin) {
				this.hybridAppService.navigateToBaseLogin();
			}
		});
	}

	startTokenRefreshInterval(): void {
		this.tokenService.setTokenRefreshInterval();
	}

	resetSessionTimeoutWatch(): void {
		this.idle.stop();
	}

	startSessionTimeoutWatch(): void {
		this.idle.watch();
		this.idle.onTimeout.subscribe(() => {
			this.modalService.closeModal(constants.modalIds.sessionTimeout);
			if (!this.isSsoLogout) {
				this.logout();
			}
		});

		this.idle.onIdleStart.subscribe(() => {
			if (!this.isSsoLogout && !this.isLoggedIn) {
				this.hybridAppService.navigateToBaseLogin();
			}
			if (!this.isSessionTimeoutModalShown) {
				this.isSessionTimeoutModalShown = true;
				this.modalService.openModal(constants.modalIds.sessionTimeout);
			}
		});
	}

	handleUserLogin(user: User): void {
		LocalStorageService.setItem(constants.localStorageKeys.CURRENT_USER, user);
		LocalStorageService.setItem(constants.localStorageKeys.TOKENS_HYBRID, {
			...LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID),
			tokens: {
				sessionToken: user.sessionToken,
				refreshToken: user.refreshToken
			}
		});
		this.currentUserSubject.next(user);
		this.startSessionTimeoutWatch();
		this.tokenService.token = user.sessionToken;
		this.isLoggedIn = true;
		this.setUserConfigs(user);
	}

	handleUserLoginWithAuth0(user: User): void {
		LocalStorageService.setItem(constants.localStorageKeys.CURRENT_USER, user);
		LocalStorageService.setItem(constants.localStorageKeys.CURRENT_USER_HYBRID, {
			userDetails: user
		});
		this.currentUserSubject.next(user);
		this.isLoggedIn = true;
		this.setUserConfigs(user);
	}

	setUserConfigs(user: User): void {
		const activeProductId = LocalStorageService.getItem(
			constants.localStorageKeys.TOKENS_HYBRID
		)?.activeProduct;

		if (
			!UserTypeService.isInternalAdmin(user?.userTypeId) &&
			activeProductId !== constants.PRODUCTS.CLIENT_CUSTOM_PATHWAY.ID
		) {
			forkJoin([
				this.userConfigService.fetchEdGoalConfiguration(user),
				this.userConfigService.fetchEdAccessConfiguration(),
				this.userConfigService.fetchProgramEligibilityAndRelatedEdAccessConfiguration(
					user
				)
			]).subscribe(
				([edGoalConfig, edAccessConfig, eligibleProgramAndEdAccessConfig]) => {
					user.hasEdGoalPrograms =
						edGoalConfig?.hasEdGoalPrograms ?
							edGoalConfig?.hasEdGoalPrograms
							: false;
					user.hasEdGoalApps = edGoalConfig?.hasEdGoalApps;
					user.hasEdGoalProgramsLoaded = edGoalConfig?.hasEdGoalProgramsLoaded;
					user.hasApprovedEdGoals = edGoalConfig?.hasApprovedEdGoals;
					user.hasEligiblePrograms = edGoalConfig?.hasEligiblePrograms;
					user.showEdGoalTile = edGoalConfig?.showEdGoalTile;
					user.edAccessEnabled = edAccessConfig?.isEdAccessEnabled;
					user.edAccessBannerDisabled =
						eligibleProgramAndEdAccessConfig?.isEdAccessBannerDisabled;
					user.edAccessProviderPageDisabled =
						eligibleProgramAndEdAccessConfig?.isEdAccessProviderPageDisabled;
					user.hasEdAccessPrograms =
						eligibleProgramAndEdAccessConfig?.hasEdAccessPrograms;
					user.policyDocuments =
						eligibleProgramAndEdAccessConfig?.policyDocuments;
					this.currentUserSubject.next(user);

					// TODO : Following updates are needed to satisfy certain pages of the old angular site.
					//  Need to be removed when all the existing PPT pages are moved under SRD work.
					const hybridUser = LocalStorageService.getItem(
						constants.localStorageKeys.CURRENT_USER_HYBRID
					);
					hybridUser.userDetails = user;
					LocalStorageService.setItem(
						constants.localStorageKeys.CURRENT_USER_HYBRID,
						hybridUser
					);
				}
			);
		}
	}

	ssoLogoutCleanup(): void {
		this.isSsoLogout = true;
		this.logoutCleanup();
	}

	getUserTypes(isExternal: boolean): Observable<Array<UserType>> {
		return this.http.get<Array<UserType>>(
			`users/userTypes?${isExternal ? 'isExternalUser' : 'isExternal'}=${isExternal}`,
			{
				context: withCache({
					clearCachePredicate: requestDataChanged
				})
			}
		);
	}

	private logoutCleanup(): void {
		this.isLoggedIn = false;
		this.resetSessionTimeoutWatch();
		SessionStorageService.clear();
		LocalStorageService.clear();
		this.currentUserSubject.next(undefined);
		this.modalService.closeModal(constants.modalIds.sessionTimeout);
	}
}
